import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInputSelect } from '@components/controlled-input-select';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/constants';
import { IconImportCSV } from '@components/icons/icon-import-csv';
import { InputCsvParser } from '@components/input-csv-parser';
import { InputSelectAdmission } from '@components/input-select-admission';
import { Loader } from '@components/loader';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import {
  ImportCSVForm,
  SPLITTER_COMMA,
  SPLITTER_SEMICOLON,
} from '@components/process-import-csv/process-import-csv.type';
import { IMPORT_CONTEXT } from '@constants/query-params';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { Admission, ADMISSION_STATUS_HIDDEN_BY_TRIGGER } from '@domain/admission';
import { ActiveChannel } from '@domain/channel';
import { useGetAdmissions } from '@hooks/admissions/use-get-admissions';
import { AdmissionResponse } from '@hooks/admissions/use-get-admissions.type';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetActiveChannels } from '@hooks/use-active-channels';

import { shouldFilterOfferIfIsSiae } from '../utils/filter-offer-if-siae';

import type { ImportCSVSelectType } from './select-type.type';

export const SelectType = ({ onContinue, offer, fileName, rows, setRows, setFileName }: ImportCSVSelectType) => {
  const { t } = useTranslation();
  const {
    companyClubState: { isSiaeImportLimited },
  } = useContext(CompanyClubContext);
  const {
    formState: { isValid },
    watch,
  } = useFormContext<ImportCSVForm>();

  const [channelList, setChannelList] = useState<ActiveChannel[]>([]);
  const SPLITTER_LIST = [
    { id: SPLITTER_COMMA, name: t('common:splitterComma') },
    { id: SPLITTER_SEMICOLON, name: t('common:splitterSemicolon') },
  ];
  const eventId = watch('eventId');
  const offerId = watch('offerId');
  const splitter = watch('splitter');
  const { data: admissionList, isLoading: isAdmissionListLoading } = useGetAdmissions({
    eventId,
    context: IMPORT_CONTEXT,
    options: { enabled: !!eventId },
  });
  const { data: activeChannels } = useGetActiveChannels({
    offerId,
    enabled: !!offerId,
  });
  const { data: eventChannels } = useGetChannels({ eventId, enabled: !!eventId && !!offerId });

  useEffect(() => {
    if (eventChannels && activeChannels && offer) {
      const channelsFormatted = eventChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          maxQuantity: null,
        }));

      if (activeChannels.isForAllChannels) {
        setChannelList(channelsFormatted);
        return;
      }

      setChannelList(activeChannels.channels);
    }
  }, [activeChannels, eventChannels, offer]);

  const filterCondition = (selectedOffer: Admission) =>
    !selectedOffer.hasMandatoryAddOns &&
    selectedOffer.status !== ADMISSION_STATUS_HIDDEN_BY_TRIGGER &&
    !selectedOffer.settings.isHidden &&
    shouldFilterOfferIfIsSiae(selectedOffer, isSiaeImportLimited);

  // We have to filter all offers with mandatory addons. Otherwise, the box office won't work
  const filteredAdmissionList = {
    bottleService: admissionList?.bottleService.filter((bottle) => filterCondition(bottle)) ?? [],
    guestList: admissionList?.guestList.filter((guest) => filterCondition(guest)) ?? [],
    ticket: admissionList?.ticket.filter((ticket) => filterCondition(ticket)) ?? [],
  };
  return (
    <>
      <ModalHeader
        title={t('common:importCsvTitle')}
        subtitle={t('eventReport:importCsvModalSubtitle')}
        primaryIcon={IconImportCSV}
      />
      <div className='flex flex-col gap-6 px-4 py-6 sm:px-20 sm:pb-12'>
        {isAdmissionListLoading && <Loader />}
        {!!admissionList && (
          <InputSelectAdmission
            admissionList={filteredAdmissionList as AdmissionResponse}
            eventId={eventId}
            selectedOffer={offer}
          />
        )}
        {offer && (
          <>
            <ControlledInputSelect
              fieldRegisterName='channelId'
              rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
              options={channelList}
              label={t('invitations:processModalChannelSelectorTitle')}
            />
            <ControlledInputSelect
              fieldRegisterName='splitter'
              rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
              options={SPLITTER_LIST}
              label={t('common:selectSplitter')}
            />
            <InputCsvParser
              delimiter={splitter}
              fileName={fileName}
              rows={rows}
              setRows={setRows}
              setFileName={setFileName}
            />
          </>
        )}
      </div>
      <ModalFooter
        isPrimaryDisabled={!isValid || rows?.length === 0 || !fileName}
        primaryButtonText={t('offers:continueButton')}
        onPrimaryClick={onContinue}
      />
    </>
  );
};
